import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { CallSplit } from '@mui/icons-material';
import { getSectionLog } from '../../features/leaderboard/leaderboardSlice';
import BSCircularProgress from '../ui/progress/circularProgress';
import { set } from 'mongoose';
import Input from '../ui/forms/fields/input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { splitSection } from '../../features/event/eventSlice';

const StyledSectionTotalTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'showBorder'
})(({ theme, showBorder }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
    borderBottom: showBorder ? '2px solid #fff' : 'none'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: showBorder ? '2px solid ' : 'none',
    borderColor: theme.palette.blue.main
  }
}));

const schema = yup.object().shape({
  splitAt: yup
    .number()
    .min(1, 'Split position must be at least 1')
    .required('Split position is required'),
  newSectionName: yup.string().required('Section name is required'),
  dividePointsBy: yup.number().when('dividePoints', {
    is: (dividePoints) => dividePoints,
    then: yup.number().required('Divide points by is required').min(1, 'Must be at least 1')
  })
});

const SplitSectionDialog = ({ event, section }) => {
  const [open, setOpen] = useState(false);
  const [newLog, setNewLog] = useState(null);
  const [loadingNewLog, setLoadingNewLog] = useState(false);
  const dispatch = useDispatch();

  const sectionLog = useSelector((state) => state.leaderboard.sectionLog);
  const loadingSectionLog = useSelector((state) => state.leaderboard.loadingSectionLog);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      splitAt: 3
    }
  });

  const watchDividePointsBy = watch('dividePointsBy');
  const watchSectionName = watch('newSectionName');
  const watchSplitAt = watch('splitAt');
  useEffect(() => {
    if (sectionLog && sectionLog.length > 0) {
      setLoadingNewLog(true);
      // Split the log into two sections
      //const mainLog = sectionLog.slice(0, watchSplitAt);
      const newLog = sectionLog.slice(watchSplitAt);
      setNewLog(newLog);
      setLoadingNewLog(false);
    }
  }, [sectionLog, watchSplitAt]);

  const handleOpen = () => {
    setOpen(true);
    handleSectionLog();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSectionLog = () => {
    dispatch(getSectionLog({ eventId: event.id, sectionId: section.id }));
  };

  const handleSplit = (data) => {
    data.eventId = event.id;
    data.sectionId = section.id;

    dispatch(splitSection(data))
      .unwrap()
      .then(() => {
        handleClose();
      })
      .catch((error) => {
        console.error('Error splitting section:', error);
      });
  };

  const mainSection = () => {
    return (
      <TableContainer
        key={section.id}
        component={Paper}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'blue.main'
        }}>
        <Table padding="none">
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  {section.name}
                </Typography>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal">Team</StyledSectionTotalTableCell>
              {/* <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Played">P</Tooltip>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Won">W</Tooltip>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Games Lost">L</Tooltip>
              </StyledSectionTotalTableCell> */}
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Points">
                  <Typography variant="p" fontWeight={'bold'} color="white">
                    Pts
                  </Typography>
                </Tooltip>
              </StyledSectionTotalTableCell>
              {/* {event.gameFormat.sets > 1 && (
                <StyledSectionTotalTableCell padding="normal" align={'center'}>
                  <Tooltip title="Total Sets Won">SW</Tooltip>
                </StyledSectionTotalTableCell>
              )} */}

              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Net Points">Net</Tooltip>
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sectionLog &&
              sectionLog.map((log, index) => (
                <TableRow key={log.teamId}>
                  <StyledSectionTotalTableCell
                    showBorder={index + 1 === Number(watchSplitAt)}
                    size="small"
                    padding="normal">
                    {index + 1}
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell
                    showBorder={index + 1 === Number(watchSplitAt)}
                    size="small"
                    padding="normal">
                    {log.teamName}
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell
                    size="small"
                    align={'center'}
                    sx={{ backgroundColor: 'blue.main' }}>
                    <Typography variant="p" fontWeight={'bold'} color="white">
                      {log.points}
                    </Typography>
                  </StyledSectionTotalTableCell>
                  <StyledSectionTotalTableCell
                    showBorder={index + 1 === Number(watchSplitAt)}
                    size="small"
                    padding="normal">
                    {log.net}
                  </StyledSectionTotalTableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      {!section.splitTo && (
        <IconButton size="small" color="success" onClick={() => handleOpen()}>
          <CallSplit />
        </IconButton>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Split Section</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" mb={2} gap={2}>
            <Input
              label={'New Section Name'}
              error={errors.newSectionName}
              helperText={errors.nreSectionName?.message}
              {...register('newSectionName')}
            />
            <Input
              label={'Split after'}
              error={errors.splitAt}
              helperText={errors.splitAt?.message}
              type="number"
              {...register('splitAt')}
            />
            <Input
              label={'Divide Points By'}
              error={errors.dividePointsBy}
              helperText={errors.dividePointsBy?.message}
              type="number"
              {...register('dividePointsBy')}
            />
          </Box>

          {loadingSectionLog ? (
            <BSCircularProgress
              caption="Loading section log..."
              size={24}
              thickness={4}
              color="primary"
            />
          ) : (
            <Box display="flex" flexDirection="row" justifyContent={'center'} p={2}>
              <Box display="flex" flexDirection="column" mr={2}>
                {mainSection()}
              </Box>
              <Box display="flex" flexDirection="column">
                {loadingNewLog ? (
                  <BSCircularProgress
                    caption="Calculating new section..."
                    size={24}
                    thickness={4}
                    color="primary"
                  />
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'blue.main'
                    }}>
                    <Table padding="none">
                      <TableHead>
                        <TableRow>
                          <StyledSectionTotalTableCell padding="normal">
                            <Typography variant="body1" fontWeight={'bold'}>
                              {watchSectionName ? watchSectionName : 'New Section'}
                            </Typography>
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal">
                            Team
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal" align={'center'}>
                            <Tooltip title="Total Points">
                              <Typography variant="p" fontWeight={'bold'} color="white">
                                Pts
                              </Typography>
                            </Tooltip>
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal" align={'center'}>
                            <Tooltip title="Total Net Points">Net</Tooltip>
                          </StyledSectionTotalTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {newLog &&
                          newLog.map((log, index) => (
                            <TableRow key={log.teamId}>
                              <StyledSectionTotalTableCell size="small" padding="normal">
                                {index + 1}
                              </StyledSectionTotalTableCell>
                              <StyledSectionTotalTableCell size="small" padding="normal">
                                {log.teamName}
                              </StyledSectionTotalTableCell>
                              <StyledSectionTotalTableCell size="small" align={'center'}>
                                {watchDividePointsBy
                                  ? (log.points / watchDividePointsBy).toFixed(2)
                                  : '0.00'}
                              </StyledSectionTotalTableCell>
                              <StyledSectionTotalTableCell size="small" padding="normal">
                                0
                              </StyledSectionTotalTableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}

          <Typography>
            <Box mt={2} fontStyle="italic" color="text.secondary">
              Note: The new section will be created with the name you provide and will contain the
              teams and scores from the split position onwards. The points will be divided by the
              value you specify.{' '}
              <Typography component="span" color="error" fontWeight={'bold'}>
                {' '}
                This can not be undone. Please ensure you have the correct section selected before
                proceeding.
              </Typography>
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => handleClose()} color="primary">
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSubmit(handleSplit)()} color="success">
            Split
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SplitSectionDialog;
